import { cn, formatMoney } from '@bemor/web-kit';
import {
    AlertsIcon,
    BankIcon,
    Card,
    CardContent,
    ForwardIcon,
    PayoutIcon,
    Skeleton,
    ThresholdsIcon,
} from '@bemor/web-kit/ui';

import type { FinanceRow } from '@/api/types';

/* BM-122: the finance report's KPI tiles, copied from Nexroll's `profit-report-kpi-cards.tsx`: the figures come from
   the report's totals (the server adds up; nothing is recomputed here beyond the chargeback rate's display). */

function signColor(value: number): string {
    if (value > 0) return 'text-success-fg';
    if (value < 0) return 'text-danger-fg';
    return 'text-fg-2';
}

function SignIcon({ value, className }: { value: number; className?: string }) {
    return (
        <ForwardIcon
            className={cn(className, value < 0 ? 'rotate-90' : '-rotate-90')}
        />
    );
}

const bps = (value: number | null | undefined) =>
    value == null ? '—' : `${(value / 100).toFixed(1)}%`;

function Tile({
    label,
    value,
    icon,
    tone,
    sub,
    testId,
    loading,
}: {
    label: string;
    value: string;
    icon: React.ReactNode;
    tone?: number;
    sub?: string;
    testId: string;
    loading: boolean;
}) {
    return (
        <Card className="gap-0 py-3" data-testid={testId}>
            <CardContent className="px-4 py-0">
                <div className="flex items-center gap-1.5">
                    <div className="bg-surface-pressed flex h-6 w-6 shrink-0 items-center justify-center rounded-md">
                        {icon}
                    </div>
                    <span className="text-fg-2 text-xs font-medium">
                        {label}
                    </span>
                </div>
                <p
                    className={cn(
                        'mt-1.5 text-lg leading-tight font-bold tabular-nums',
                        tone === undefined ? 'text-fg-1' : signColor(tone),
                    )}
                >
                    {loading ? <Skeleton className="h-[1em] w-24" /> : value}
                </p>
                {sub ? <p className="text-fg-2 text-xs">{sub}</p> : null}
            </CardContent>
        </Card>
    );
}

export function FinanceKpiCards({
    totals,
    loading,
}: {
    totals: FinanceRow | undefined;
    loading: boolean;
}) {
    const t = totals;
    const margin = t?.margin_actual_cents ?? t?.margin_expected_cents ?? 0;
    const chargebackRate =
        t && t.orders > 0
            ? Math.round((t.chargebacks / t.orders) * 10000)
            : null;
    return (
        <div
            className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5"
            data-testid="finance-kpis"
        >
            <Tile
                label="Income"
                value={formatMoney(t?.income_cents ?? 0, 'USD')}
                icon={<PayoutIcon className="text-fg-2 h-3.5 w-3.5" />}
                sub="sale, refund and chargeback fees"
                testId="kpi-income"
                loading={loading}
            />
            <Tile
                label="Processing cost"
                value={formatMoney(
                    t?.actual_cost_cents ?? t?.expected_cost_cents ?? 0,
                    'USD',
                )}
                icon={<BankIcon className="text-fg-2 h-3.5 w-3.5" />}
                sub={
                    t?.actual_cost_cents == null
                        ? 'expected (nothing settled yet)'
                        : `actual, ${t.settled_orders} settled`
                }
                testId="kpi-cost"
                loading={loading}
            />
            <Tile
                label="Margin"
                value={formatMoney(margin, 'USD')}
                icon={
                    <SignIcon
                        value={margin}
                        className={cn('h-3.5 w-3.5', signColor(margin))}
                    />
                }
                tone={margin}
                sub={
                    t?.margin_actual_cents == null
                        ? `expected ${formatMoney(t?.margin_expected_cents ?? 0, 'USD')}`
                        : `expected ${formatMoney(t.margin_expected_cents, 'USD')}`
                }
                testId="kpi-margin"
                loading={loading}
            />
            <Tile
                label="Margin rate"
                value={bps(t?.margin_actual_bps ?? t?.margin_expected_bps)}
                icon={<ThresholdsIcon className="text-fg-2 h-3.5 w-3.5" />}
                tone={t?.margin_actual_bps ?? t?.margin_expected_bps ?? 0}
                sub="of income"
                testId="kpi-margin-rate"
                loading={loading}
            />
            <Tile
                label="Chargeback rate"
                value={bps(chargebackRate)}
                icon={<AlertsIcon className="text-fg-2 h-3.5 w-3.5" />}
                sub={t ? `${t.chargebacks} of ${t.orders} orders` : undefined}
                testId="kpi-chargebacks"
                loading={loading}
            />
        </div>
    );
}
